import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { KeepAliveApi } from '@ClientApi/keepAliveApi';
import { getCurrentUrlWithCompanyAppended } from '../utils/url';


const DEFAULT_TIMER = 30000;
const SHOW_WARNING_TIMER = 120000;

export const useTimeoutManager = () => {
    const router = useLocation();
    let timeout: NodeJS.Timeout | null = null;
    let warningTimer: NodeJS.Timeout | null = null;
    let count502s = 0;

    const logOut = (clientReason: string) => {
        // we should redirect them to the "/login" window after
        // the session has expired WITH the current URL.
        const url = getCurrentUrlWithCompanyAppended();
        window.location.href = `/logout?next=${encodeURIComponent(url)}&reason=${clientReason}`;
    }

    const showWarning = () => {
        toast.warning(`Hey! You've been inactive for a little too long. For security purposes, we will be logging you out in a couple of minutes.`);
    }

    const restartAutoReset = async () => {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setInterval(async () => {
            try {
                const data = await KeepAliveApi.get() as unknown as { expiry: { expiresIn: string, expiresOn: string } };
                const expiry = data.expiry;
                if (!expiry) {
                    return;
                }
    
                const expiresIn = expiry.expiresIn;
    
                if (warningTimer) {
                    clearTimeout(warningTimer);
                }
    
                warningTimer = setTimeout(() => {
                    showWarning();
                }, Number(expiresIn) - SHOW_WARNING_TIMER)
    
            } catch (err) {
                if (err === 502) {
                    // Since we periodically receive 502s from the load balancer during deploys
                    // we shouldn't log the user out on a single 502. Instead, we'll keep a count
                    // and log the user out if 3 or more 502s are received in a row.
                    count502s++;
                    if (count502s >= 3) {
                        return logOut('keep_alive:three_consecutive_502s');
                    }
                } else if (err) {
                    const errorString = typeof err === 'object' ? JSON.stringify(err) : err;
                    return logOut(`keep_alive:error:${errorString}`);
                } else {
                    count502s = 0;
                }
            }
        }, DEFAULT_TIMER);
    }

    const onMouseOver = () => { restartAutoReset(); }
    const onKeyUp = () => { restartAutoReset(); }
    const onMouseMove = () => { restartAutoReset(); }
    const onTouchEnd = () => { restartAutoReset(); }
    const onTouchMove = () => { restartAutoReset(); }

    React.useEffect(() => {
        //initiate timeout
        restartAutoReset();

        //listen for events
        window.addEventListener('mouseover', onMouseOver);
        window.addEventListener('keyup', onKeyUp);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('touchend', onTouchEnd);
        window.addEventListener('touchmove', onTouchMove);

        //cleanup
        return () => {
            if (timeout) {
                clearTimeout(timeout);
                window.removeEventListener('mouseover', onMouseOver);
                window.removeEventListener('keyup', onKeyUp);
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('touchend', onTouchEnd);
                window.removeEventListener('touchmove', onTouchMove);
            }
        }
    }, [router.pathname]) 
}